'use client'

import { useCallback, useEffect, useState } from 'react'
import { useDocumentInfo } from '@payloadcms/ui'

// Client intake + data room panel — mounted as a UI field on the Projects
// edit view (below the PRD panel, same in-flow admin-card slot). Shows the
// questionnaire status, sends/re-sends the tokenized link, and lists the
// files the client has uploaded to their data room.

const STATUS_LABEL = {
  'not-sent': 'Not Sent',
  sent: 'Sent',
  'in-progress': 'In Progress',
  submitted: 'Submitted',
}

const STATUS_COLOR = {
  'not-sent': 'var(--theme-elevation-500)',
  sent: 'var(--theme-elevation-500)',
  'in-progress': 'var(--theme-warning-500, #b26a00)',
  submitted: 'var(--theme-success-500, #2e7d32)',
}

const card = {
  border: '1px solid var(--theme-elevation-150)',
  borderRadius: '8px',
  padding: '16px 20px',
  marginBottom: '24px',
  background: 'var(--theme-elevation-50)',
}

const btn = {
  padding: '6px 14px',
  borderRadius: '6px',
  border: '1px solid var(--theme-elevation-250)',
  background: 'var(--theme-elevation-0)',
  cursor: 'pointer',
  fontSize: '13px',
}

function ClientIntakePanelClient({ projectId }) {
  const [loading, setLoading] = useState(true)
  const [intake, setIntake] = useState(null)
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)
  const [copied, setCopied] = useState(false)

  const loadState = useCallback(async () => {
    setLoading(true)
    try {
      const [intakeRes, filesRes] = await Promise.all([
        fetch(
          `/payload-api/client-intakes?where[project][equals]=${encodeURIComponent(projectId)}&limit=1&depth=0`,
          { credentials: 'include' },
        ),
        fetch(
          `/payload-api/client-files?where[project][equals]=${encodeURIComponent(projectId)}&sort=-createdAt&limit=100&depth=0`,
          { credentials: 'include' },
        ),
      ])
      const intakeData = intakeRes.ok ? await intakeRes.json() : null
      const filesData = filesRes.ok ? await filesRes.json() : null
      setIntake(intakeData?.docs?.[0] || null)
      setFiles(filesData?.docs || [])
    } catch {
      /* panel is best-effort chrome — the doc view still works without it */
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    loadState()
  }, [loadState])

  async function sendIntake() {
    setBusy(true)
    setMessage(null)
    try {
      const res = await fetch('/api/client-intake/send', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data?.ok) {
        setMessage({
          ok: false,
          text:
            data?.reason === 'no-founder-email'
              ? 'No client email on file — add a client contact on the project (or link a Z2H application), then try again.'
              : data?.error || `HTTP ${res.status}`,
        })
        return
      }
      setMessage({ ok: true, text: 'Questionnaire sent.' })
      await loadState()
    } catch (err) {
      setMessage({ ok: false, text: err.message })
    } finally {
      setBusy(false)
    }
  }

  function copyLink() {
    if (!intake?.token) return
    const url = `${window.location.origin}/client-intake/${intake.token}`
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  if (loading) {
    return <div style={card}>Loading client intake…</div>
  }

  const status = intake?.status || 'not-sent'
  const answered =
    intake?.answers && typeof intake.answers === 'object'
      ? Object.keys(intake.answers).length
      : 0

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 14 }}>Client Intake &amp; Data Room</strong>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: STATUS_COLOR[status],
            border: `1px solid ${STATUS_COLOR[status]}`,
            borderRadius: 999,
            padding: '1px 10px',
          }}
        >
          {STATUS_LABEL[status]}
        </span>
        {intake?.sentAt && (
          <span style={{ fontSize: 12, color: 'var(--theme-elevation-500)' }}>
            sent {new Date(intake.sentAt).toLocaleDateString()}
          </span>
        )}
        {intake?.submittedAt && (
          <span style={{ fontSize: 12, color: 'var(--theme-elevation-500)' }}>
            submitted {new Date(intake.submittedAt).toLocaleDateString()} · {answered} answers
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button type="button" style={btn} onClick={sendIntake} disabled={busy}>
          {busy ? 'Sending…' : status === 'not-sent' ? 'Send questionnaire' : 'Re-send questionnaire'}
        </button>
        {intake?.token && (
          <button type="button" style={btn} onClick={copyLink}>
            {copied ? 'Copied ✓' : 'Copy client link'}
          </button>
        )}
        {intake && (
          <a
            href={`/admin/collections/client-intakes/${intake.id}`}
            style={{ ...btn, textDecoration: 'none', display: 'inline-block', lineHeight: '20px' }}
          >
            View answers
          </a>
        )}
      </div>

      {message && (
        <p
          style={{
            marginTop: 10,
            marginBottom: 0,
            fontSize: 13,
            color: message.ok ? 'var(--theme-success-500, #2e7d32)' : 'var(--theme-error-500, #b3261e)',
          }}
        >
          {message.text}
        </p>
      )}

      <div style={{ marginTop: 14 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--theme-elevation-500)' }}>
          DATA ROOM · {files.length} file{files.length === 1 ? '' : 's'}
        </span>
        {files.length > 0 && (
          <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
            {files.slice(0, 8).map((f) => (
              <li key={f.id} style={{ fontSize: 13, padding: '3px 0' }}>
                <a href={`/admin/collections/client-files/${f.id}`}>{f.filename}</a>
                <span style={{ color: 'var(--theme-elevation-500)', marginLeft: 8, fontSize: 12 }}>
                  {f.uploadedBy === 'client' ? 'from client' : 'team'} ·{' '}
                  {new Date(f.createdAt).toLocaleDateString()}
                </span>
              </li>
            ))}
            {files.length > 8 && (
              <li style={{ fontSize: 12, color: 'var(--theme-elevation-500)', paddingTop: 4 }}>
                <a href={`/admin/collections/client-files?where[project][equals]=${projectId}`}>
                  …and {files.length - 8} more
                </a>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  )
}

export function ClientIntakePanel() {
  const { id } = useDocumentInfo()
  if (!id) return null
  return <ClientIntakePanelClient projectId={id} />
}
